import {
    collapseAllExpandedGroups,
    commitTableDataFromGrid,
    createGroupLoadingRow,
    getChildRowsFromGrid,
    getParentDataFromGrid,
    getParentRowIndexInGrid,
    isParentGroupExpanded,
    patchParentRowInGrid,
    refreshParentRow,
    runWithoutGroupRowAnimation,
    syncTableDataFromGrid,
} from '../../utils/groupRowExpansion';

export default function Chevron(params) {
    if (!params.withGroup) return null;
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10"><path d="M5.586 5L2.374 8.67a.5.5 0 10.752.66l3.496-3.995a.499.499 0 000-.67L3.126.671a.5.5 0 10-.752.658L5.586 5z" fill="#B5BDC5" fill-rule="evenodd"/></svg>
    `;

    const chevron = document.createElement('div');
    chevron.classList.add('w5-group-chevron');

    if (!params.data.isGroup) {
        chevron.classList.add('w5-group-chevron--no-group');
        return chevron;
    }
    chevron.innerHTML = svg;

    chevron.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const { api, node, onRequest, children, context } = params;
        const data = node?.data ?? params.data;
        if (!data?.isGroup) return;
        if (data.isLoading) return;

        if (isParentGroupExpanded(api, data.id)) {
            onContract(data.id, api, context);
        } else {
            onExpand(data.id, api, onRequest, children, context);
        }
    });

    return chevron;
}

function onGetChildren(parentId, api, onRequest, children, context) {
    return new Promise((resolve, reject) => {
        if (onRequest) {
            const loadingRow = createGroupLoadingRow(parentId);
            const parentIndex = getParentRowIndexInGrid(api, parentId);

            if (parentIndex < 0) {
                reject();
                return;
            }

            runWithoutGroupRowAnimation(api, context, () => {
                patchParentRowInGrid(api, parentId, { isGroupOpen: true, isLoading: true });
                api.applyTransaction({
                    add: [loadingRow],
                    addIndex: parentIndex + 1,
                });
            });
            refreshParentRow(api, parentId);
            syncTableDataFromGrid(api, context);

            onRequest(parentId)
                .then((response) => {
                    resolve(response);
                })
                .catch(() => {
                    runWithoutGroupRowAnimation(api, context, () => {
                        api.applyTransaction({ remove: [loadingRow] });
                        patchParentRowInGrid(api, parentId, {
                            isGroupOpen: false,
                            isLoading: undefined,
                        });
                    });
                    refreshParentRow(api, parentId);
                    commitTableDataFromGrid(api, context);
                    reject();
                });
        } else if (children) resolve(children);
        else reject();
    });
}

function onExpand(parentId, api, onRequest, children, context) {
    const liveParent = getParentDataFromGrid(api, parentId);
    if (liveParent?.isLoading) return;
    if (isParentGroupExpanded(api, parentId)) return;

    const loadingRow = createGroupLoadingRow(parentId);

    onGetChildren(parentId, api, onRequest, children, context).then(
        (response) => {
            if (!response || !Array.isArray(response) || response.length === 0) {
                runWithoutGroupRowAnimation(api, context, () => {
                    api.applyTransaction({ remove: [loadingRow] });
                    patchParentRowInGrid(api, parentId, {
                        isGroupOpen: false,
                        isLoading: undefined,
                    });
                });
                refreshParentRow(api, parentId);
                commitTableDataFromGrid(api, context);
                return;
            }

            const parentIndex = getParentRowIndexInGrid(api, parentId);
            if (parentIndex < 0) {
                runWithoutGroupRowAnimation(api, context, () => {
                    api.applyTransaction({ remove: [loadingRow] });
                    patchParentRowInGrid(api, parentId, {
                        isGroupOpen: false,
                        isLoading: undefined,
                    });
                });
                refreshParentRow(api, parentId);
                commitTableDataFromGrid(api, context);
                return;
            }

            const staleChildren = getChildRowsFromGrid(api, parentId);

            runWithoutGroupRowAnimation(api, context, () => {
                if (staleChildren.length > 0) {
                    api.applyTransaction({ remove: staleChildren });
                }
                patchParentRowInGrid(api, parentId, {
                    isGroupOpen: true,
                    isLoading: undefined,
                    children: response,
                });
                api.applyTransaction({
                    remove: [loadingRow],
                    add: response,
                    addIndex: parentIndex + 1,
                });
            });
            refreshParentRow(api, parentId);
            commitTableDataFromGrid(api, context);
        },
        () => {
            runWithoutGroupRowAnimation(api, context, () => {
                api.applyTransaction({ remove: [loadingRow] });
                patchParentRowInGrid(api, parentId, {
                    isGroupOpen: false,
                    isLoading: undefined,
                });
            });
            refreshParentRow(api, parentId);
            commitTableDataFromGrid(api, context);
        },
    );
}

function onContract(parentId, api, context) {
    if (!isParentGroupExpanded(api, parentId)) return;

    const children = getChildRowsFromGrid(api, parentId);

    runWithoutGroupRowAnimation(api, context, () => {
        patchParentRowInGrid(api, parentId, {
            isGroupOpen: false,
            children: undefined,
            isLoading: undefined,
        });
        if (children.length > 0) {
            api.applyTransaction({ remove: children });
        }
    });
    refreshParentRow(api, parentId);
    commitTableDataFromGrid(api, context);
}

export function removeChildrenOnDrag(api, onCancelGroupRequests, commitGridRowData, tableData) {
    onCancelGroupRequests?.();
    const collapsed = collapseAllExpandedGroups(tableData);
    commitGridRowData?.(collapsed);
    api?.setGridOption('rowData', collapsed);
}
